import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import publicApi from "../../apis/publicApi";
import "./ResetPassword.css";

import {
    IoLockClosedOutline,
    IoEyeOutline,
    IoEyeOffOutline,
    IoCheckmarkCircleOutline,
    IoAlertCircleOutline,
    IoArrowBackOutline,
    IoShieldCheckmarkOutline,
} from "react-icons/io5";

// Password strength helper
const getStrength = (pw) => {
    if (!pw) return { score: 0, label: "", cls: "" };

    let score = 0;

    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;

    if (score <= 1)
        return { score, label: "Weak", cls: "cp-strength--weak" };

    if (score <= 3)
        return { score, label: "Fair", cls: "cp-strength--fair" };

    if (score === 4)
        return { score, label: "Good", cls: "cp-strength--good" };

    return { score, label: "Strong", cls: "cp-strength--strong" };
};

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    // Token verification
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState("");

    // Form state
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [success, setSuccess] = useState(false);

    const strength = getStrength(newPassword);

    useEffect(() => {
        let isMounted = true;

        const verifyToken = async () => {
            if (!token) {
                if (!isMounted) return;

                setTokenError(
                    "No reset token found. Please request a new reset link."
                );
                setTokenValid(false);
                setVerifying(false);

                return;
            }

            try {
                await publicApi.get(
                    `/api/auth/verify-reset-token?token=${token}`
                );

                if (!isMounted) return;

                setTokenValid(true);
            } catch (err) {
                if (!isMounted) return;

                setTokenError(
                    err.response?.data?.message ||
                    "This link is invalid or has expired. Please request a new one."
                );

                setTokenValid(false);
            } finally {
                if (isMounted) {
                    setVerifying(false);
                }
            }
        };

        verifyToken();

        return () => {
            isMounted = false;
        };
    }, [token]);

    const validate = () => {
        const validationErrors = {};

        if (!newPassword) {
            validationErrors.newPassword = "New password is required.";
        } else if (newPassword.length < 8) {
            validationErrors.newPassword =
                "Password must be at least 8 characters.";
        }

        if (!confirmPassword) {
            validationErrors.confirmPassword =
                "Please confirm your password.";
        } else if (newPassword !== confirmPassword) {
            validationErrors.confirmPassword =
                "Passwords do not match.";
        }

        return validationErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitting) return;

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            setSubmitting(true);
            setSubmitError("");

            await publicApi.post("/api/auth/reset-password", {
                token,
                newPassword,
            });

            setSuccess(true);

            setTimeout(() => {
                navigate("/login");
            }, 4000);
        } catch (err) {
            setSubmitError(
                err.response?.data?.message ||
                "Failed to reset password. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Loading State
    if (verifying) {
        return (
            <div className="cp-root">
                <div className="cp-container">
                    <div className="cp-verifying">
                        <div className="cp-verifying__spinner" />
                        <p>Verifying reset link…</p>
                    </div>
                </div>
            </div>
        );
    }

    // Invalid Token State
    if (!tokenValid) {
        return (
            <div className="cp-root">
                <div className="cp-container">
                    <Link to="/" className="cp-brand">
                        <div className="cp-brand__icon">
                            <IoLockClosedOutline size={20} />
                        </div>
                        <span className="cp-brand__name">Budget Tracker</span>
                    </Link>

                    <div className="cp-card cp-card--error">
                        <div className="cp-error-icon">
                            <IoAlertCircleOutline size={36} />
                        </div>

                        <h1 className="cp-card__title">
                            Link Invalid or Expired
                        </h1>

                        <p className="cp-card__subtitle">
                            {tokenError}
                        </p>

                        <Link
                            to="/forgot-password"
                            className="cp-btn cp-btn--primary"
                        >
                            Request New Reset Link
                        </Link>

                        <Link
                            to="/login"
                            className="cp-back-link"
                        >
                            <IoArrowBackOutline size={13} />
                            Back to Sign In
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Success State
    if (success) {
        return (
            <div className="cp-root">
                <div className="cp-container">
                    <Link to="/" className="cp-brand">
                        <div className="cp-brand__icon">
                            <IoLockClosedOutline size={20} />
                        </div>
                        <span className="cp-brand__name">Budget Tracker</span>
                    </Link>

                    <div className="cp-card">
                        <div className="cp-success-icon">
                            <IoCheckmarkCircleOutline size={40} />
                        </div>

                        <h1 className="cp-card__title">
                            Password Reset!
                        </h1>

                        <p className="cp-card__subtitle">
                            Your password has been updated successfully.
                            You'll be redirected to the sign-in page
                            shortly.
                        </p>

                        <Link
                            to="/login"
                            className="cp-btn cp-btn--primary"
                        >
                            Sign In Now
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cp-root">
            <div className="cp-container">
                <Link to="/" className="cp-brand">
                    <div className="cp-brand__icon">
                        <IoLockClosedOutline size={20} />
                    </div>
                    <span className="cp-brand__name">Budget Tracker</span>
                </Link>

                <div className="cp-card">
                    <div className="cp-card__header">
                        <div className="cp-card__header-icon">
                            <IoShieldCheckmarkOutline size={22} />
                        </div>

                        <div>
                            <h1 className="cp-card__title">
                                Reset Your Password
                            </h1>

                            <p className="cp-card__subtitle">
                                Choose a new, strong password for your
                                account.
                            </p>
                        </div>
                    </div>

                    {submitError && (
                        <div className="cp-alert cp-alert--error">
                            <IoAlertCircleOutline size={15} />
                            {submitError}
                        </div>
                    )}

                    <form
                        className="cp-form"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        {/* New Password */}
                        <div
                            className={`cp-field ${errors.newPassword ? "cp-field--error" : ""
                                }`}
                        >
                            <label
                                htmlFor="cp-new"
                                className="cp-field__label"
                            >
                                New Password
                                <span className="cp-field__required">*</span>
                            </label>

                            <div className="cp-field__input-wrap">
                                <IoLockClosedOutline
                                    className="cp-field__icon"
                                    size={15}
                                />

                                <input
                                    id="cp-new"
                                    type={showNew ? "text" : "password"}
                                    className="cp-input"
                                    placeholder="Minimum 8 characters..."
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);

                                        setErrors((prev) => ({
                                            ...prev,
                                            newPassword: "",
                                        }));
                                    }}
                                    autoComplete="new-password"
                                    autoFocus
                                />

                                <button
                                    type="button"
                                    className="cp-eye-btn"
                                    onClick={() =>
                                        setShowNew((prev) => !prev)
                                    }
                                >
                                    {showNew ? (
                                        <IoEyeOffOutline size={16} />
                                    ) : (
                                        <IoEyeOutline size={16} />
                                    )}
                                </button>
                            </div>

                            {newPassword && (
                                <div className="cp-strength">
                                    <div className="cp-strength__bars">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <div
                                                key={n}
                                                className={`cp-strength__bar ${strength.score >= n
                                                        ? strength.cls
                                                        : ""
                                                    }`}
                                            />
                                        ))}
                                    </div>

                                    <span
                                        className={`cp-strength__label ${strength.cls}`}
                                    >
                                        {strength.label}
                                    </span>
                                </div>
                            )}

                            {errors.newPassword && (
                                <p className="cp-field__error">
                                    {errors.newPassword}
                                </p>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div
                            className={`cp-field ${errors.confirmPassword
                                    ? "cp-field--error"
                                    : ""
                                }`}
                        >
                            <label
                                htmlFor="cp-confirm"
                                className="cp-field__label"
                            >
                                Confirm New Password
                                <span className="cp-field__required">*</span>
                            </label>

                            <div className="cp-field__input-wrap">
                                <IoLockClosedOutline
                                    className="cp-field__icon"
                                    size={15}
                                />

                                <input
                                    id="cp-confirm"
                                    type={
                                        showConfirm ? "text" : "password"
                                    }
                                    className="cp-input"
                                    placeholder="Re-enter your new password..."
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(
                                            e.target.value
                                        );

                                        setErrors((prev) => ({
                                            ...prev,
                                            confirmPassword: "",
                                        }));
                                    }}
                                    autoComplete="new-password"
                                />

                                <button
                                    type="button"
                                    className="cp-eye-btn"
                                    onClick={() =>
                                        setShowConfirm((prev) => !prev)
                                    }
                                >
                                    {showConfirm ? (
                                        <IoEyeOffOutline size={16} />
                                    ) : (
                                        <IoEyeOutline size={16} />
                                    )}
                                </button>
                            </div>

                            {confirmPassword && (
                                <div
                                    className={`cp-match ${newPassword === confirmPassword
                                            ? "cp-match--ok"
                                            : "cp-match--no"
                                        }`}
                                >
                                    {newPassword === confirmPassword ? (
                                        <>
                                            <IoCheckmarkCircleOutline
                                                size={13}
                                            />
                                            Passwords match
                                        </>
                                    ) : (
                                        <>
                                            <IoAlertCircleOutline
                                                size={13}
                                            />
                                            Passwords do not match
                                        </>
                                    )}
                                </div>
                            )}

                            {errors.confirmPassword && (
                                <p className="cp-field__error">
                                    {errors.confirmPassword}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="cp-btn cp-btn--primary"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <span className="cp-spinner" />
                            ) : (
                                "Set New Password"
                            )}
                        </button>
                    </form>

                    <Link
                        to="/login"
                        className="cp-back-link"
                    >
                        <IoArrowBackOutline size={13} />
                        Back to Sign In
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;